import { StateBox, WeakestLinkBanner } from '@garnet/ui';
import { captureKind, captureProgress, requiredCaptures } from '../data/shape.js';
import type { InspectorTask } from '../data/types.js';
import { CardLabel, PhoneButton } from '../components/PhoneChrome.js';
import { Scanner } from '../components/Scanner.js';
import { CaptureField } from '../components/CaptureField.js';

/**
 * Capture — method-templated evidence collection (PRD §10.3, §14.3). The required captures
 * for the task's method are rendered from METHOD_REQUIRED_CAPTURES via `requiredCaptures`;
 * each is collected with the control its kind demands. A carrier scanner heads the screen
 * when the template requires one. The Review button is disabled until the gate is met.
 */
export function Capture({
  task,
  captured,
  values,
  onToggle,
  onValue,
  onReview,
}: {
  task: InspectorTask | null;
  captured: ReadonlySet<string>;
  values: Record<string, string>;
  onToggle: (key: string) => void;
  onValue: (key: string, value: string) => void;
  onReview: () => void;
}) {
  if (!task) {
    return (
      <div style={{ padding: 14 }}>
        <StateBox kind="empty" title="No task open">Accept a task to begin capture.</StateBox>
      </div>
    );
  }

  const required = requiredCaptures(task.method);
  const progress = captureProgress(task.method, captured);
  const scanKey = required.find((k) => captureKind(k) === 'scan');
  const listKeys = required.filter((k) => k !== scanKey);

  return (
    <div style={{ padding: 14, display: 'grid', gap: 14 }}>
      <div style={{ background: 'var(--panel)', border: '1px solid var(--rule)', borderRadius: 10, padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div>
            <CardLabel>{task.id} · {task.method_label}</CardLabel>
            <div style={{ fontWeight: 600, fontSize: 14, marginTop: 5 }}>{task.asset}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>{task.supplier}</div>
          </div>
          <span
            role="status"
            aria-label={`${progress.done} of ${progress.total} required captures complete`}
            style={{
              flex: 'none',
              fontFamily: 'var(--mono)',
              fontSize: 11,
              fontWeight: 600,
              padding: '4px 9px',
              borderRadius: 999,
              background: progress.complete ? 'var(--verified-bg)' : 'var(--neutral-bg)',
              color: progress.complete ? 'var(--verified-ink)' : 'var(--muted)',
            }}
          >
            {progress.done}/{progress.total} captured
          </span>
        </div>
        <div style={{ display: 'flex', gap: 3, marginTop: 11 }} aria-hidden="true">
          {Array.from({ length: progress.total }, (_, i) => (
            <span
              key={i}
              style={{
                flex: 1,
                height: 5,
                borderRadius: 3,
                background: i < progress.done ? 'var(--verified)' : 'var(--rule)',
              }}
            />
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {scanKey && <Scanner carrier={task.carrier} scanned={captured.has(scanKey)} onScan={() => onToggle(scanKey)} />}

        <div style={{ background: 'var(--panel)', border: '1px solid var(--rule)', borderRadius: 10, overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 12px',
              borderBottom: '1px solid var(--rule)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
            }}
          >
            <span>required captures · {task.method_label}</span>
            <span>{progress.done}/{progress.total}</span>
          </div>
          {listKeys.map((key) => (
            <CaptureField
              key={key}
              capKey={key}
              kind={captureKind(key)}
              value={values[key] ?? ''}
              done={captured.has(key)}
              onToggle={() => onToggle(key)}
              onValue={(v) => onValue(key, v)}
            />
          ))}
        </div>
      </div>

      <WeakestLinkBanner tone="attest">
        A signed capture resolves <strong>{task.resolves_check_label}</strong> to verified · attestation (gold).
      </WeakestLinkBanner>

      <PhoneButton primary full disabled={!progress.complete} onClick={onReview}>
        {progress.complete ? 'Review & sign' : `${progress.missing.length} capture${progress.missing.length === 1 ? '' : 's'} remaining`}
      </PhoneButton>
    </div>
  );
}
